import { useState, useEffect } from 'react';
import { getEntryById, getEntryChildren, getEntryDiagrams } from '../../../api/metaRegistriesApi';
import { getDiagrams } from '../../../api/diagramsApi';
import { getSystemInfrastructure } from '../../../api/systemsApi';
import { getNamespaceDetails } from '../../../api/discoveryApi';

export const useEntryDetails = (selected, registrySlug, uiProfile, projectId) => {
  const [relLoading, setRelLoading] = useState(false);
  const [relError, setRelError] = useState('');
  const [related, setRelated] = useState([]);
  const [allDiagrams, setAllDiagrams] = useState([]);
  const [children, setChildren] = useState([]);
  const [childrenLoading, setChildrenLoading] = useState(false);
  const [infrastructure, setInfrastructure] = useState(null);
  const [infraLoading, setInfraLoading] = useState(false);
  const [namespaceDetails, setNamespaceDetails] = useState(null); // {namespace, pods, services, deployments}
  const [namespaceLoading, setNamespaceLoading] = useState(false);
  const infraEnabled = !!uiProfile?.features?.infrastructureDetails;

  const loadFullEntryInfo = async (entryId) => {
    try {
      return await getEntryById(projectId, registrySlug, entryId);
    } catch (e) {
      console.error('Ошибка загрузки записи:', e);
      return null;
    }
  };

  // Load related diagrams
  useEffect(() => {
    let mounted = true;
    setRelError('');
    setRelated([]);
    if (!selected || !registrySlug) return () => { mounted = false; };

    setRelLoading(true);
    getEntryDiagrams(projectId, registrySlug, selected.id)
      .then((list) => {
        if (mounted) {
          setRelated(list.map(d => ({ id: d.doc_id, userId: d.user_id, meta: d.meta })));
        }
      })
      .catch((e) => { if (mounted) setRelError(e.message || String(e)); })
      .finally(() => { if (mounted) setRelLoading(false); });

    return () => { mounted = false; };
  }, [selected, registrySlug, projectId]);

  // Load all diagrams for the selector
  useEffect(() => {
    let mounted = true;
    if (!selected) return () => { mounted = false; };

    Promise.resolve(projectId ? getDiagrams(projectId) : Promise.resolve([]))
      .then((list) => { if (mounted) setAllDiagrams((list || []).map(d => ({ id: d.doc_id ?? d.id, meta: d.meta }))); })
      .catch(() => { if (mounted) setAllDiagrams([]); });

    return () => { mounted = false; };
  }, [selected, projectId]);

  // Load children entries
  useEffect(() => {
    let mounted = true;
    setChildren([]);
    if (!selected || !registrySlug) return () => { mounted = false; };

    setChildrenLoading(true);
    getEntryChildren(projectId, registrySlug, selected.id)
      .then((list) => { if (mounted) setChildren(list || []); })
      .catch(() => { if (mounted) setChildren([]); })
      .finally(() => { if (mounted) setChildrenLoading(false); });

    return () => { mounted = false; };
  }, [selected, registrySlug, projectId]);

  // Load Kubernetes infrastructure by entry code
  useEffect(() => {
    let mounted = true;
    if (!infraEnabled || !selected?.code) {
      setInfrastructure(null);
      setInfraLoading(false);
      return () => { mounted = false; };
    }

    setInfraLoading(true);
    getSystemInfrastructure(selected.code)
      .then(data => { if (mounted) setInfrastructure(data || null); })
      .catch(() => { if (mounted) setInfrastructure(null); })
      .finally(() => { if (mounted) setInfraLoading(false); });

    return () => { mounted = false; };
  }, [selected, infraEnabled]);

  // Load namespace details when entry is bound to a specific namespace
  useEffect(() => {
    let mounted = true;
    if (!infraEnabled) {
      setNamespaceDetails(null);
      setNamespaceLoading(false);
      return () => { mounted = false; };
    }

    const attrs = selected?.attributes || {};
    const clusterId = attrs.k8s_cluster_id;
    const nsName = attrs.k8s_namespace;

    if (!clusterId || !nsName || attrs.k8s_resource_type !== 'namespace') {
      setNamespaceDetails(null);
      setNamespaceLoading(false);
      return () => { mounted = false; };
    }

    setNamespaceLoading(true);
    getNamespaceDetails(projectId, clusterId, nsName)
      .then(data => { if (mounted) setNamespaceDetails(data || null); })
      .catch(() => { if (mounted) setNamespaceDetails(null); })
      .finally(() => { if (mounted) setNamespaceLoading(false); });

    return () => { mounted = false; };
  }, [selected, infraEnabled]);

  return {
    relLoading,
    relError,
    related,
    setRelated,
    allDiagrams,
    loadFullEntryInfo,
    children,
    childrenLoading,
    setChildren,
    infrastructure,
    infraLoading,
    namespaceDetails,
    namespaceLoading,
  };
};
